// [Codex 2026-07-09] Browser-native speech bridge for Voice CAO.

import { resolvePreferredAssistantVoice } from "./voice-speech-voices";
import {
  speakDesktopAssistantReply,
  stopDesktopAssistantReply,
} from "./voice-local-tts";

interface NativeSpeechRecognitionAlternative {
  transcript: string;
}

interface NativeSpeechRecognitionResult {
  isFinal: boolean;
  [index: number]: NativeSpeechRecognitionAlternative | undefined;
}

interface NativeSpeechRecognitionResultList {
  length: number;
  [index: number]: NativeSpeechRecognitionResult | undefined;
}

interface NativeSpeechRecognitionResultEvent extends Event {
  resultIndex: number;
  results: NativeSpeechRecognitionResultList;
}

interface NativeSpeechRecognitionErrorEvent extends Event {
  error?: string;
  message?: string;
}

interface NativeSpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: NativeSpeechRecognitionResultEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: NativeSpeechRecognitionErrorEvent) => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface NativeSpeechRecognitionConstructor {
  new (): NativeSpeechRecognition;
}

interface NativeSpeechWindow extends Window {
  SpeechRecognition?: NativeSpeechRecognitionConstructor;
  webkitSpeechRecognition?: NativeSpeechRecognitionConstructor;
}

export interface NativeSpeechRecognizer {
  start: () => boolean;
  stop: () => void;
  abort: () => void;
}

interface NativeSpeechRecognizerInput {
  onTranscript: (transcript: string) => void;
  onEnd: () => void;
  onError: (message: string) => void;
}

interface SpeakAssistantReplyInput {
  text: string;
  enabled: boolean;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (message: string) => void;
}

export function isNativeSpeechRecognitionSupported(): boolean {
  return getSpeechRecognitionConstructor() !== null;
}

export function isDesktopLocalSpeechRecognitionAvailable(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.electron?.voiceStt?.transcribe === "function"
  );
}

export function isNativeSpeechSynthesisSupported(): boolean {
  if (typeof window === "undefined") return false;
  return "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
}

export function createNativeSpeechRecognizer({
  onTranscript,
  onEnd,
  onError,
}: NativeSpeechRecognizerInput): NativeSpeechRecognizer | null {
  const Recognition = getSpeechRecognitionConstructor();
  if (!Recognition) return null;

  const recognition = new Recognition();
  recognition.continuous = false;
  recognition.interimResults = false;
  recognition.lang = "en-US";
  recognition.maxAlternatives = 1;
  recognition.onresult = (event) => {
    const transcript = readFinalTranscript(event);
    if (transcript) onTranscript(transcript);
  };
  recognition.onerror = (event) => {
    onError(event.message || event.error || "Speech recognition failed");
  };
  recognition.onend = onEnd;

  return {
    start: () => {
      try {
        recognition.start();
        return true;
      } catch (error) {
        onError(error instanceof Error ? error.message : String(error));
        return false;
      }
    },
    stop: () => {
      try {
        recognition.stop();
      } catch {
        /* already stopped */
      }
    },
    abort: () => {
      try {
        recognition.abort();
      } catch {
        /* already stopped */
      }
    },
  };
}

export async function speakAssistantReply({
  text,
  enabled,
  onStart,
  onEnd,
  onError,
}: SpeakAssistantReplyInput): Promise<boolean> {
  const spokenText = normalizeSpeechText(text);
  if (!enabled || !spokenText) return false;
  let agentVolumeTimer: number | null = null;
  const startAgentSpeech = () => {
    agentVolumeTimer = startAgentVoiceVolumePulse();
    onStart?.();
  };
  const finishAgentSpeech = () => {
    if (agentVolumeTimer) window.clearInterval(agentVolumeTimer);
    agentVolumeTimer = null;
    dispatchAgentVoiceVolume(0);
    onEnd?.();
  };

  const didSpeakWithDesktop = await speakDesktopAssistantReply({
    text: spokenText,
    onStart: startAgentSpeech,
    onEnd: finishAgentSpeech,
    onError,
  });
  if (didSpeakWithDesktop) return true;
  if (!isNativeSpeechSynthesisSupported()) return false;

  const preferredVoice = await resolvePreferredAssistantVoice(
    window.speechSynthesis,
  );

  return new Promise((resolve) => {
    const utterance = new SpeechSynthesisUtterance(spokenText);
    let didStart = false;
    let didResolve = false;
    let timeout: number | null = null;
    const finish = (result: boolean) => {
      if (didResolve) return;
      didResolve = true;
      if (timeout) window.clearTimeout(timeout);
      finishAgentSpeech();
      resolve(result);
    };
    timeout = window.setTimeout(
      () => finish(didStart),
      Math.min(45_000, Math.max(5_000, spokenText.length * 80)),
    );

    if (preferredVoice) utterance.voice = preferredVoice;
    utterance.lang = preferredVoice?.lang || "en-GB";
    utterance.rate = 0.94;
    utterance.pitch = 1.02;
    utterance.volume = 0.96;
    utterance.onstart = () => {
      didStart = true;
      startAgentSpeech();
    };
    utterance.onend = () => {
      finish(true);
    };
    utterance.onerror = (event) => {
      const message =
        "error" in event && typeof event.error === "string"
          ? event.error
          : "Speech synthesis failed";
      onError?.(message);
      finish(didStart);
    };

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  });
}

export function stopNativeSpeech(): void {
  stopDesktopAssistantReply();
  if (!isNativeSpeechSynthesisSupported()) return;
  window.speechSynthesis.cancel();
}

function getSpeechRecognitionConstructor(): NativeSpeechRecognitionConstructor | null {
  if (typeof window === "undefined") return null;
  const speechWindow = window as NativeSpeechWindow;
  return (
    speechWindow.SpeechRecognition ??
    speechWindow.webkitSpeechRecognition ??
    null
  );
}

function readFinalTranscript(
  event: NativeSpeechRecognitionResultEvent,
): string {
  const pieces: string[] = [];
  for (
    let index = event.resultIndex;
    index < event.results.length;
    index += 1
  ) {
    const result = event.results[index];
    if (!result?.isFinal) continue;
    const transcript = result[0]?.transcript?.trim();
    if (transcript) pieces.push(transcript);
  }
  return pieces.join(" ").trim();
}

function normalizeSpeechText(text: string): string {
  return text.replace(/\s+/g, " ").trim().slice(0, 2800);
}

function startAgentVoiceVolumePulse(): number {
  let frame = 0;
  dispatchAgentVoiceVolume(0.42);
  return window.setInterval(() => {
    const wave = Math.sin(frame / 2.2) * 0.22;
    const phrase = frame % 9 === 8 ? 0.18 : 0.52;
    dispatchAgentVoiceVolume(Math.max(0.16, Math.min(0.92, phrase + wave)));
    frame += 1;
  }, 90);
}

function dispatchAgentVoiceVolume(volume: number): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("fintheon:voice-cao-agent-volume", {
      detail: {
        volume: Math.max(0, Math.min(1, volume)),
      },
    }),
  );
}
